// js/core/Physics.js
// 固定步長物理：路線＝導航意圖，人潮把黎先生推離路線（看得見計畫線與實際線分岔＝可理解失敗）。
//
// 模型摘要：
//   desired = 朝下一航點的期望速度
//   steering = desired - vel，受 turn_resistance 限制（急轎＝失速偏離）
//   合力 = 該格人潮力 ＋ 側向人潮力 × wind_affinity
//   積分後做地形／危險物／收集物／目的地碰撞判定

import { dist, distSeg, pointInPoly } from '../util/geom.js';

const ARRIVE = 0.55;        // 抵達航點半徑（格）
const MAX_SPEED_MULT = 1.3;
const RESTITUTION = 0.45;   // 碰地形反彈保留速度
const HIT_DMG = 11;         // 每次擦撞地形耐久
const PEN_DMG = 26;         // 整個穿入地形耐久
const BOAT_R = 0.5;         // 行人碰撞半徑（格）
const BOUNDS = { minX: 0.3, maxX: 19.7, minY: 0.3, maxY: 11.7 };

function zoneAt(x, y, zones) {
  for (const z of zones) {
    if (x >= z.x && x <= z.x + z.w && y >= z.y && y <= z.y + z.h) return z;
  }
  return null;
}

// 回傳本步產生的事件陣列（供上層判定勝負／播放效果）
export function step(boat, route, level, dt) {
  const events = [];
  if (boat.delivered || boat.failed) return events;

  // --- 1. 選定目標航點（已抵達的往前跳過）---
  let target = null;
  while (boat.wp < route.points.length) {
    const p = route.points[boat.wp];
    if (dist(boat.x, boat.y, p.x, p.y) <= ARRIVE) { boat.wp++; continue; }
    target = p;
    break;
  }

  // --- 2. 期望速度（朝目標）---
  let desX = 0, desY = 0;
  if (target) {
    const dx = target.x - boat.x, dy = target.y - boat.y;
    const d = Math.hypot(dx, dy) || 1;
    const spd = boat.attrs.speed * (boat.boostT > 0 ? 1.6 : 1);  // 咖啡因衝刺
    desX = (dx / d) * spd;
    desY = (dy / d) * spd;
  }

  // --- 3. 轉向（受 turn_resistance 限制；油漬區內轉向近乎失靈）---
  const steerX = desX - boat.vx;
  const steerY = desY - boat.vy;
  // turn_resistance 越高 → 轉向率越低（反應越遲鈍）
  const inOil = zoneAt(boat.x, boat.y, level.oilZones || []) != null;
  const turnRate = ((1 - boat.attrs.turn_resistance) * 7 + 2.5) * (inOil ? 0.22 : 1);
  const sLen = Math.hypot(steerX, steerY);
  const maxSteer = turnRate * dt;
  let sx = steerX, sy = steerY;
  if (sLen > maxSteer) { sx = (steerX / sLen) * maxSteer; sy = (steerY / sLen) * maxSteer; }
  boat.vx += sx;
  boat.vy += sy;

  // --- 4. 外力：人潮、側向人潮 ---
  const cur = zoneAt(boat.x, boat.y, level.currents || []);
  if (cur) { boat.vx += cur.fx * dt; boat.vy += cur.fy * dt; }
  const wnd = zoneAt(boat.x, boat.y, level.windZones || []);
  if (wnd) {
    const wa = boat.attrs.wind_affinity;
    boat.vx += wnd.fx * wa * dt;
    boat.vy += wnd.fy * wa * dt;
  }
  // 迴旋人潮：圓形切線力＋輕微內吸（黎先生會被捲住打轉）
  for (const vz of level.currents || []) {
    if (!vz.vortex) continue;
    const v = vz.vortex;
    const dx = boat.x - v.cx, dy = boat.y - v.cy;
    const d = Math.hypot(dx, dy);
    if (d < v.r && d > 0.05) {
      const tx = -dy / d, ty = dx / d;
      boat.vx += (tx * v.s - (dx / d) * v.s * 0.22) * dt;
      boat.vy += (ty * v.s - (dy / d) * v.s * 0.22) * dt;
    }
  }

  // --- 5. 阻尼＋限速 ---
  const damp = Math.exp(-0.55 * dt);
  boat.vx *= damp;
  boat.vy *= damp;
  const maxSpeed = boat.attrs.speed * MAX_SPEED_MULT * (boat.boostT > 0 ? 1.6 : 1);
  if (boat.boostT > 0) boat.boostT -= dt;
  const sp = Math.hypot(boat.vx, boat.vy);
  if (sp > maxSpeed) { boat.vx = (boat.vx / sp) * maxSpeed; boat.vy = (boat.vy / sp) * maxSpeed; }

  // --- 6. 積分 ---
  boat.x += boat.vx * dt;
  boat.y += boat.vy * dt;
  if (sp > 0.05) boat.heading = Math.atan2(boat.vy, boat.vx);

  // --- 7. 尾跡取樣 ---
  boat._trailT += dt;
  if (boat._trailT > 0.03) {
    boat._trailT = 0;
    const last = boat.trail[boat.trail.length - 1];
    if (!last || dist(last.x, last.y, boat.x, boat.y) > 0.06) boat.trail.push({ x: boat.x, y: boat.y });
  }

  // --- 8. 地形碰撞（閘機關閉中＝當牆計；開啟＝可通過） ---
  const walls = [...(level.terrain || [])];
  for (const g of level.gates || []) {
    if (g.active !== false) walls.push(g.poly);
  }
  for (const poly of walls) {
    if (pointInPoly(boat.x, boat.y, poly)) {
      // 整個穿入：退出並重傷
      boat.collisions++;
      boat.durability -= PEN_DMG;
      boat.x -= boat.vx * dt * 1.5;
      boat.y -= boat.vy * dt * 1.5;
      boat.vx *= -RESTITUTION;
      boat.vy *= -RESTITUTION;
      events.push({ type: 'collision' });
      continue;
    }
    // 邊緣擦撞：找最近邊，沿法線推出並反射
    let best = null, bestD = BOAT_R;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const d = distSeg(boat.x, boat.y, poly[j].x, poly[j].y, poly[i].x, poly[i].y);
      if (d < bestD) { bestD = d; best = { j, i }; }
    }
    if (best) {
      const a = poly[best.j], b = poly[best.i];
      const ex = b.x - a.x, ey = b.y - a.y;
      const el = Math.hypot(ex, ey) || 1;
      let nx = -ey / el, ny = ex / el;
      const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
      if ((boat.x - cx) * nx + (boat.y - cy) * ny < 0) { nx = -nx; ny = -ny; }
      const pen = BOAT_R - bestD;
      boat.x += nx * pen;
      boat.y += ny * pen;
      const vn = boat.vx * nx + boat.vy * ny;
      if (vn < 0) {
        boat.vx -= (1 + RESTITUTION) * vn * nx;
        boat.vy -= (1 + RESTITUTION) * vn * ny;
        boat.collisions++;
        boat.durability -= HIT_DMG;
        events.push({ type: 'collision' });
      }
    }
  }

  // --- 8.5 彈彈椅：彈開不扣血、不計碰撞 ---
  for (const bp of level.bumpers || []) {
    const dx = boat.x - bp.x, dy = boat.y - bp.y;
    const d = Math.hypot(dx, dy) || 0.01;
    const minD = bp.r + BOAT_R;
    if (d < minD) {
      const nx = dx / d, ny = dy / d;
      boat.x = bp.x + nx * (minD + 0.02);
      boat.y = bp.y + ny * (minD + 0.02);
      const vn = boat.vx * nx + boat.vy * ny;
      if (vn < 0) { boat.vx -= 1.7 * vn * nx; boat.vy -= 1.7 * vn * ny; }
      boat.vx += nx * (bp.power || 5) * 0.55;
      boat.vy += ny * (bp.power || 5) * 0.55;
      events.push({ type: 'bump' });
    }
  }

  // --- 9. 危險物（紅綠燈病菌熄燈中＝可安全通過；分裂病菌被撞後一分二） ---
  const spawns = [];
  for (const hz of level.hazards) {
    if (hz.hit || hz.active === false) continue;
    if (dist(boat.x, boat.y, hz.x, hz.y) <= hz.r + BOAT_R) {
      hz.hit = true;
      boat.durability -= hz.damage;
      boat.collisions++;
      events.push({ type: 'hazard', damage: hz.damage });
      // 分裂：垂直航向彈開兩舊細菌（不放巡邏／週期，不再連鎖分裂）
      if (hz.split && (hz.gen || 0) < hz.split.maxGen) {
        const ang = boat.heading + Math.PI / 2;
        for (const s of [1, -1]) {
          spawns.push({
            ...hz,
            hit: false,
            gen: (hz.gen || 0) + 1,
            r: hz.r * 0.75,
            damage: Math.round(hz.damage * 0.5),
            x: hz.x + Math.cos(ang) * 1.5 * s,
            y: hz.y + Math.sin(ang) * 1.5 * s,
            patrol: undefined,
            period: undefined,
            split: undefined,
          });
        }
      }
    }
  }
  for (const sp of spawns) level.hazards.push(sp);

  // --- 10. 收集物 ---
  for (const c of level.collectibles) {
    if (c.collected) continue;
    if (dist(boat.x, boat.y, c.x, c.y) <= c.r + BOAT_R) {
      c.collected = true;
      boat.collected.push(c.id);
      // 凍檸茶補給：回體力
      if (c.heal) {
        boat.durability = Math.min(boat.maxDurability, boat.durability + c.heal);
        events.push({ type: 'heal', amount: c.heal });
      }
      // 咖啡因：限時衝刺
      if (c.boost) {
        boat.boostT += c.boost;
        events.push({ type: 'boost', amount: c.boost });
      }
      events.push({ type: 'collect', id: c.id });
    }
  }

  // --- 11. 目的地（多站＝順序到訪，最後一站先算打卡） ---
  if (!boat.delivered) {
    const dests = level.destinations || [];
    if (dests.length > 1) {
      const d = dests[boat.stopIdx];
      if (d && !d.done && dist(boat.x, boat.y, d.x, d.y) <= d.r) {
        d.done = true;
        boat.stopIdx++;
        events.push({ type: 'stop', id: d.id, idx: boat.stopIdx });
        if (boat.stopIdx >= dests.length) {
          boat.delivered = true;
          events.push({ type: 'deliver', id: d.id });
        }
      }
    } else {
      for (const d of dests) {
        if (dist(boat.x, boat.y, d.x, d.y) <= d.r) {
          boat.delivered = true;
          events.push({ type: 'deliver', id: d.id });
          break;
        }
      }
    }
  }

  // --- 12. 失敗判定 ---
  if (boat.x < BOUNDS.minX || boat.x > BOUNDS.maxX || boat.y < BOUNDS.minY || boat.y > BOUNDS.maxY) {
    boat.failed = true;
    events.push({ type: 'fail', reason: 'out of bounds' });
  }
  if (boat.durability <= 0) {
    boat.durability = 0;
    boat.failed = true;
    events.push({ type: 'fail', reason: 'durability' });
  }

  return events;
}
